import {
  TeContainer,
  TeContainerSlim,
  TeCallout,
  TeButtonMinimal,
  TePlaceholderContainer,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { Section } from '../../utils/styling';
import EmptyCompanies from './EmptyCompanies';
import CompanyTree, { CompanyTreeItem } from '../../components/Company/CompanyTree';
import ViewContainer from '../../components/ViewContainer';
import { COMPANIES_CREATED_LIMIT } from '../../app/constants';
import CancelCompanyModal from './CancelCompanyModal';
import { useCurrentIdentityRequestBusinessPartners } from './companies.queries';
import { useCurrentIdentityBusinessPartners } from '../Settings/businessPartner.queries';
import { useMigrationCandidateBusinessPartners } from '../../hooks/queries/migration.queries';
import CompanyDivisionModal from '../../components/Modal/CompanyDivisionModal';
import LimitReachedModal from '../../components/Modal/LimitReachedModal';
import {
  BusinessPartnerAccessabilityEnum,
  BusinessPartnerWithCurrentIdentityInfoBody,
} from '../../api/generated/pap';
import {
  RequestBusinessPartnerBody,
  RequestBusinessPartnerStatusEnum,
} from '../../api/generated/pm';
import featureFlags from '../../utils/featureFlags';
import { AppRoutes } from '../../app/routes/utils';

const CalloutWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1280px;
`;

const CalloutAction = styled.div`
  position: absolute;
  right: 48px;
  top: 50%;
  transform: translateY(-50%);
`;

function CompaniesList() {
  const { t } = useTranslation('companies', { keyPrefix: 'list' });
  const { t: tCommon } = useTranslation('common');
  const [searchValue, setSearchValue] = useState('');
  const { data: businessPartners, isFetched: isFetchedBusinessPartners } =
    useCurrentIdentityBusinessPartners();
  const {
    data: requestBusinessPartners,
    isFetched: isFetchedRequestBusinessPartners,
    refetch,
  } = useCurrentIdentityRequestBusinessPartners();
  const lowerCaseSearch = searchValue.toLowerCase();
  const [selectedCompany, setSelectedCompany] = useState<CompanyTreeItem | null>(null);
  const [isSelectedCompany, setIsSelectedCompany] = useState<boolean>(false);
  const [cancelCompanyModalOpen, setCancelCompanyModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const { data: migrationCandidates } = useMigrationCandidateBusinessPartners();
  const [isCalloutDismissed, setIsCalloutDismissed] = useState(false);
  const navigate = useNavigate();

  const hasMigrationCandidates =
    featureFlags.MIGRATION &&
    !isCalloutDismissed &&
    migrationCandidates?.items &&
    migrationCandidates.items.length > 0;

  const openCreateModal = () => setCreateModalOpen(true);

  const onCreateModalClose = (shouldUpdate: boolean) => {
    setCreateModalOpen(false);
    if (shouldUpdate) refetch();
  };

  const openCancelCompanyModal = (company: CompanyTreeItem, isCompany: boolean) => {
    setSelectedCompany(company);
    setIsSelectedCompany(isCompany);
    setCancelCompanyModalOpen(true);
  };

  const closeCancelCompanyModal = (shouldUpdate: boolean) => {
    setSelectedCompany(null);
    setCancelCompanyModalOpen(false);
    if (shouldUpdate) refetch();
  };

  const createdCompaniesCount =
    (requestBusinessPartners?.items.length || 0) +
    (businessPartners?.items?.filter((businessPartner) => businessPartner?.isOwner).length || 0);

  const combinedBusinessPartners = useMemo(() => {
    if (!requestBusinessPartners?.items && !businessPartners?.items) {
      return [];
    }

    const requestPartners = (requestBusinessPartners?.items || [])
      .filter(
        (partner) =>
          partner.status !== RequestBusinessPartnerStatusEnum.ACCEPTED &&
          partner.status !== RequestBusinessPartnerStatusEnum.DONE
      )
      .map(
        (
          partner
        ): BusinessPartnerWithCurrentIdentityInfoBody &
          Pick<RequestBusinessPartnerBody, 'companyAddress'> => ({
          ...partner,
          accessability: BusinessPartnerAccessabilityEnum.REQUEST_TO_JOIN,
          archived: false,
          extId: '',
          iconURL: undefined,
          isOwner: false,
          isSameDomain: false,
          modifiedAt: partner.createdAt,
          ownerIdentityId: partner.ownerIdentityId,
          trn: '',
          type: partner.type,
          requestToJoin: {
            businessPartnerId: partner.id,
            createdAt: partner.createdAt,
            id: partner.id,
            identityId: partner.ownerIdentityId,
            modifiedAt: partner.modifiedAt,
          },
        })
      );

    return [...requestPartners, ...(businessPartners?.items || [])].sort((a, b) =>
      a.name.localeCompare(b.name, 'fi', { numeric: true })
    );
  }, [businessPartners, requestBusinessPartners]);

  const filteredCompanies = useMemo(() => {
    return combinedBusinessPartners.filter(({ id, name }) => {
      const nameMatch = name.toLowerCase().includes(lowerCaseSearch);
      const childMatch = combinedBusinessPartners
        .filter(({ parentBusinessPartnerId }) => id === parentBusinessPartnerId)
        .some(({ name }) => name.toLowerCase().includes(lowerCaseSearch));
      return nameMatch || childMatch;
    });
  }, [lowerCaseSearch, combinedBusinessPartners]);

  const companiesCount = useMemo(() => {
    const ids = new Set(
      combinedBusinessPartners.map(({ id }) => id).filter((id): id is string => !!id)
    );
    return combinedBusinessPartners.reduce((acc, { parentBusinessPartnerId }) => {
      const hasVisibleParent = parentBusinessPartnerId && ids.has(parentBusinessPartnerId);
      return acc + Number(!hasVisibleParent);
    }, 0);
  }, [combinedBusinessPartners]);

  const filteredCount = useMemo(() => {
    const ids = new Set(filteredCompanies.map(({ id }) => id).filter((id): id is string => !!id));
    return filteredCompanies.reduce((acc, { parentBusinessPartnerId }) => {
      const hasVisibleParent = parentBusinessPartnerId && ids.has(parentBusinessPartnerId);
      return acc + Number(!hasVisibleParent);
    }, 0);
  }, [filteredCompanies]);

  const noContent =
    !isFetchedBusinessPartners ||
    !isFetchedRequestBusinessPartners ||
    combinedBusinessPartners.length <= 0 ||
    filteredCount <= 0;

  const renderLoader = () => {
    return <TePlaceholderContainer loader text={t('loadingCompanies')} />;
  };

  const renderNoCompanies = () => {
    return (
      <EmptyCompanies
        onClick={
          createdCompaniesCount >= COMPANIES_CREATED_LIMIT
            ? () => setLimitModalOpen(true)
            : openCreateModal
        }
      />
    );
  };

  const renderNoContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={4}
        column
        fill
        justifyContent="center"
        alignItems="center"
      >
        {!isFetchedBusinessPartners || !isFetchedRequestBusinessPartners ? (
          renderLoader()
        ) : !companiesCount ? (
          renderNoCompanies()
        ) : (
          <TePlaceholderContainer text={t('noSearchResults')} />
        )}
      </TeContainerSlim>
    );
  };

  return (
    <TeContainerSlim column grow padding="xs" overflow="auto" alignItems="center">
      <Section>
        <ViewContainer
          title={t('title')}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          noSeparator
          visibleItemsText={`( ${filteredCount} )`}
          searchPlaceholder={t('searchPlaceholder')}
          createNewItemText={t('createCompany')}
          onCreateNewItemClick={
            createdCompaniesCount >= COMPANIES_CREATED_LIMIT
              ? () => setLimitModalOpen(true)
              : openCreateModal
          }
          buttonDisabled={!isFetchedBusinessPartners}
          noPadding
        >
          {hasMigrationCandidates && (
            <CalloutWrapper>
              <TeCallout
                status="info"
                icon="info"
                heading={tCommon('suggestedOrganizations.heading')}
                text={tCommon('suggestedOrganizations.description')}
                closeable
                teclose={() => setIsCalloutDismissed(true)}
                grow
                style={{ borderColor: '#005FAD', marginBottom: 8 }}
              />
              <CalloutAction>
                <TeButtonMinimal
                  text={tCommon('suggestedOrganizations.reviewSuggestions')}
                  size="s"
                  teclick={() => navigate(AppRoutes.UnclaimedOrganizations)}
                />
              </CalloutAction>
            </CalloutWrapper>
          )}
          <TeContainer
            backgroundColor="background.primary"
            overflow="hidden"
            borderRadius={4}
            fill
            column
          >
            {noContent ? (
              renderNoContent()
            ) : (
              <CompanyTree
                type="companies"
                allowSignIn
                showRoles={true}
                companies={filteredCompanies}
                searchValue={searchValue}
                onUpdate={refetch}
                onCancelRequest={openCancelCompanyModal}
              />
            )}
          </TeContainer>
        </ViewContainer>
      </Section>

      {createModalOpen && <CompanyDivisionModal open onClose={onCreateModalClose} />}

      {selectedCompany && (
        <CancelCompanyModal
          open={cancelCompanyModalOpen}
          company={selectedCompany}
          isCompany={isSelectedCompany}
          onClose={closeCancelCompanyModal}
        />
      )}

      <LimitReachedModal
        title={t('companyLimitReached')}
        description={t('companyLimitReachedDescription', {
          max: COMPANIES_CREATED_LIMIT,
        })}
        icon="business"
        open={limitModalOpen}
        setOpen={setLimitModalOpen}
      />
    </TeContainerSlim>
  );
}

export default CompaniesList;
